import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import useEffectiveRolePath from "../../../hooks/useEffectiveRolePath";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import { ArrowBack, Analytics, TrendingUp, Schedule, Group } from "@mui/icons-material";
import { useTimetableAnalytics } from "../../../hooks/queries/useTimetable";

const TimetableAnalytics = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { rolePath } = useEffectiveRolePath();
  const { data: analyticsData, isLoading, error } = useTimetableAnalytics(id);

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        gap={2}
      >
        <CircularProgress size={48} />
        <Typography variant="body2" color="text.secondary">
          Loading timetable data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load analytics: {error.message}
        </Alert>
      </Box>
    );
  }

  const analytics = analyticsData?.data?.data || analyticsData?.data || {};

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate(`${rolePath}/timetable`)}
          >
            Back to Timetables
          </Button>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              <Analytics sx={{ mr: 1, verticalAlign: "middle" }} />
              Timetable Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Detailed statistics and quality metrics for timetable #{id}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Analytics Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Schedule sx={{ fontSize: 48, color: "primary.main", mb: 1 }} />
              <Typography variant="h4" fontWeight={600}>
                {analytics.totalSlots || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Slots
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Group sx={{ fontSize: 48, color: "success.main", mb: 1 }} />
              <Typography variant="h4" fontWeight={600}>
                {analytics.teacherLoad ? Object.keys(analytics.teacherLoad).length : 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Teachers
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <TrendingUp sx={{ fontSize: 48, color: "info.main", mb: 1 }} />
              <Typography variant="h4" fontWeight={600}>
                {analytics.avgSlotsPerDay ? analytics.avgSlotsPerDay.toFixed(1) : "N/A"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avg Slots/Day
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Analytics sx={{ fontSize: 48, color: "warning.main", mb: 1 }} />
              <Typography variant="h4" fontWeight={600}>
                {analytics.utilizationRate || "N/A"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Utilization Rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Analytics */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Slot Distribution by Day
              </Typography>
              {analytics.dayDistribution ? (
                <Box>
                  {Object.entries(analytics.dayDistribution).map(([day, count]) => (
                    <Box key={day} sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2">{day}:</Typography>
                      <Chip label={`${count} slots`} size="small" />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No distribution data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
                <Group sx={{ mr: 1 }} />
                Teacher Workload
              </Typography>
              {analytics.teacherLoad ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {Object.entries(analytics.teacherLoad)
                    .sort(([,a], [,b]) => b.total - a.total) // Sort by total slots descending
                    .map(([teacherId, data]) => (
                      <Box key={teacherId} sx={{ 
                        p: 2, 
                        border: '1px solid #e0e0e0', 
                        borderRadius: 2,
                        bgcolor: 'grey.50'
                      }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            Teacher
                          </Typography>
                          <Chip 
                            label={`${data.total} slots`} 
                            size="small" 
                            color={data.total > 4 ? "success" : data.total > 2 ? "warning" : "info"}
                          />
                        </Box>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {Object.entries(data.byDay).map(([day, count]) => (
                            <Chip 
                              key={day} 
                              label={`${day}: ${count}`} 
                              size="small" 
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No workload data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
                <Analytics sx={{ mr: 1 }} />
                Class Workload
              </Typography>
              {analytics.classLoad ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {Object.entries(analytics.classLoad)
                    .sort(([,a], [,b]) => b.total - a.total) // Sort by total slots descending
                    .map(([classId, data]) => (
                      <Box key={classId} sx={{ 
                        p: 2, 
                        border: '1px solid #e0e0e0', 
                        borderRadius: 2,
                        bgcolor: 'grey.50'
                      }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            Class
                          </Typography>
                          <Chip 
                            label={`${data.total} slots`} 
                            size="small" 
                            color={data.total > 4 ? "success" : data.total > 2 ? "warning" : "info"}
                          />
                        </Box>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {Object.entries(data.byDay).map(([day, count]) => (
                            <Chip 
                              key={day} 
                              label={`${day}: ${count}`} 
                              size="small" 
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No class workload data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TimetableAnalytics;